/**
 * The read catalog — this Worker's entire request surface.
 *
 * ProductData already exposes the full admin API (142 routes); the Next.js
 * layer this app replaces was a pass-through in front of it. So the console's
 * backend is not business logic, it is an allowlist: a declared route is
 * forwarded to ProductData over the service binding, and anything undeclared
 * is a 404 before authentication even matters.
 *
 * Two rules make the allowlist meaningful rather than decorative:
 *
 *   1. GET only. The console is read-only by design — operators mutate through
 *      the admin MCP server, which has its own catalog, its own exclusions, and
 *      its own audit trail. A method other than GET is not "not yet supported"
 *      here; it is out of scope, and adding one is a deliberate decision.
 *      "Read-only" means no customer data changes, not that nothing is written:
 *      three catalogued company GETs append an `activity_log` row naming the
 *      admin who looked (ProductData's `AUDIT_ONLY_ADMIN_GETS`, bounded to that
 *      one row and pinned there), and upstream refuses this Worker on any GET
 *      that enqueues or mutates — see `apps/admin/src/worker/AGENTS.md`.
 *   2. Query keys are allowlisted per entry. An undeclared key is dropped, not
 *      forwarded, so a crafted request cannot reach a ProductData filter this
 *      console never intended to expose.
 *   3. A `:param` never stands in for an upstream STATIC segment. A bare
 *      `:param` matches any segment, so a declared detail entry would
 *      otherwise also reach a route nobody listed — see `shadowedParamValues`
 *      below and `upstream-siblings.ts`.
 */
import { SPLIT_READ_ROUTES } from "./catalog-split";
import { UPSTREAM_STATIC_SIBLINGS } from "./upstream-siblings";

export interface ReadRoute {
  /** Hono path pattern; `:name` segments are forwarded positionally. */
  readonly path: string;
  /** Query keys forwarded to ProductData. Everything else is dropped. */
  readonly query: readonly string[];
  /**
   * This entry answers a FILE, not JSON, so it forwards through `forwardFile`
   * and keeps upstream's `Content-Disposition`. Only the report exports set it.
   */
  readonly file?: true;
}

const PAGINATION = ["page", "pageSize"] as const;

/**
 * Wave 1: dashboard, orders, users.
 *
 * Chosen because between them they exercise every mechanic the remaining
 * features need — a cached aggregate, a paginated list with filters and
 * sorting, a detail drilldown, and the Better Auth identity join — while
 * covering only 11 of the 141 catalogued admin actions. Companies (43 actions)
 * is deliberately wave 2: it is the largest surface in the app and should ride
 * rails that are already proven, not prove them.
 */

/**
 * The report engine's own reads (spec §8), one entry per report NAME.
 *
 * A `:report` param would have been shorter and is the wrong shape twice over:
 * it would need a surveyed `UPSTREAM_STATIC_SIBLINGS` key for a prefix that
 * itself contains a param, and it would silently accept any segment upstream
 * ever adds under `/reports/`. Static entries are the allowlist doing its job.
 *
 * The names are the `ReportName` union from spec §8.1, spelled verbatim, and
 * they are SNAKE_CASE for a reason that outlives style: `profit-and-loss` and
 * `balance-sheet` below are plan 01-05's own routes on a different pair of
 * handlers, and the report engine's route must not collide with them. Both
 * spellings appear in this file deliberately, and the two grains never share a
 * path.
 */
export const REPORT_PATH_SEGMENTS = [
  "profit_and_loss",
  "balance_sheet",
  "trial_balance",
  "general_ledger",
  "journal",
  "cash_summary",
  "account_transactions",
] as const;

/**
 * `ReportRequest` (spec §8.1) minus `bookId` and `report`, which are the path.
 * `compareFrom`/`compareTo` carry the object form of `compare`; the console
 * sends the string form today and declares both so a custom comparison range
 * does not need a catalog change to ship.
 */
const REPORT_QUERY = [
  "from",
  "to",
  "asAt",
  "preset",
  "compare",
  "compareFrom",
  "compareTo",
  "basis",
  "includeZero",
  "accountId",
  // Round 4 (M6): the general ledger's period picker.
  "period",
  "cursor",
  "limit",
] as const;

/**
 * One entry shape needs `forwardFile`: the report export (spec §8.2), which
 * streams CSV or PDF with a `Content-Disposition` the API composed from the
 * company name, the report and the period.
 */
function reportRoutes(): ReadRoute[] {
  return REPORT_PATH_SEGMENTS.flatMap((report) => [
    {
      path: `/api/admin/bookkeeping/books/:bookId/reports/${report}`,
      query: [...REPORT_QUERY],
    },
    {
      // `format=csv|pdf`. A file, so it forwards through `forwardFile`.
      path: `/api/admin/bookkeeping/books/:bookId/reports/${report}/export`,
      query: [...REPORT_QUERY, "format"],
      file: true,
    },
  ]);
}

export const READ_ROUTES: readonly ReadRoute[] = [
  // --- dashboard -----------------------------------------------------------
  { path: "/api/admin/dashboard", query: [] },

  // --- orders --------------------------------------------------------------
  {
    path: "/api/admin/orders",
    query: [
      ...PAGINATION,
      "search",
      "order_type",
      "status",
      "payment_status",
      "package_type",
      "company_id",
      "auth_user_id",
      "created_after",
      "created_before",
      "formation_status_group",
      "undelivered_paid",
      "sort_by",
      "sort_order",
    ],
  },
  { path: "/api/admin/orders/tab-counts", query: [] },
  {
    path: "/api/admin/orders/work-queue",
    query: [...PAGINATION, "search", "kind"],
  },
  { path: "/api/admin/orders/:id", query: ["include_documents"] },

  // --- users ---------------------------------------------------------------
  {
    path: "/api/admin/users",
    query: [...PAGINATION, "search", "tab", "role", "onboarding_status"],
  },
  { path: "/api/admin/users/:userId", query: [] },

  // --- companies (wave 2) ---------------------------------------------------
  // Query keys mirror apps/operator/src/catalog-data.ts verbatim. GET only;
  // per-id sub-resource GETs (documents/:docId, members/:memberId,
  // addresses/:addressId) serve edit flows this console does not have, and
  // notes ride `?include=` on the detail route instead of their own path.
  {
    path: "/api/admin/companies",
    query: [
      ...PAGINATION,
      "search",
      "company_type",
      "formation_state",
      "status",
      "onboarding_status",
    ],
  },
  { path: "/api/admin/companies/:companyId", query: ["include"] },
  { path: "/api/admin/companies/:companyId/addresses", query: [] },
  {
    path: "/api/admin/companies/:companyId/documents",
    query: ["category", "source", "file_type"],
  },
  { path: "/api/admin/companies/:companyId/docuseal-submissions", query: [] },
  { path: "/api/admin/companies/:companyId/entitlements", query: [] },
  { path: "/api/admin/companies/:companyId/services/federal-tax", query: [] },
  { path: "/api/admin/companies/:companyId/formation-shareholders", query: [] },
  { path: "/api/admin/companies/:companyId/services/mailroom", query: [] },
  { path: "/api/admin/companies/:companyId/members", query: [] },
  {
    path: "/api/admin/companies/:companyId/payments",
    query: [
      "format",
      "payment_method",
      "status",
      "product_type",
      "date_from",
      "date_to",
      "page",
      "limit",
      "sort_by",
      "sort_order",
    ],
  },
  { path: "/api/admin/companies/:companyId/shareholders", query: [] },
  {
    path: "/api/admin/companies/:companyId/services/registered-agent",
    query: [],
  },
  { path: "/api/admin/companies/:companyId/services/tax-filings", query: [] },
  // State-registry phase 2 (2026-09-30): the company page's State record
  // section and the work queue's State records list. Pure D1 reads upstream,
  // so the read-only marker never trips them.
  { path: "/api/admin/companies/:companyId/state-record", query: [] },
  {
    path: "/api/admin/companies/:companyId/wire-transfer-subscription",
    query: [],
  },

  // --- documents, mailroom, docuseal (wave 3) ------------------------------
  // Query keys are ProductData's own handler params, NOT the mcp-admin
  // catalog's: that catalog was extracted from the Vercel admin API, which
  // forwards `limit`/`sortBy`/`query` verbatim into a ProductData handler that
  // reads `pageSize`/`sort_by`/`search` and ignores the rest. Mirroring it here
  // would ship a sort control that does nothing.
  {
    path: "/api/admin/documents",
    query: [
      ...PAGINATION,
      "search",
      "category",
      "company_id",
      "sort_by",
      "sort_order",
    ],
  },
  // The id is constrained to digits, and that constraint is load-bearing, not
  // cosmetic. A bare `:id` matches ANY single segment, so ProductData's static
  // siblings — `/documents/storage-readiness`, `/documents/signed-url`,
  // `/documents/signed-url-by-path`, `/mailroom/subscriptions` — would ride the
  // detail entry and be forwarded verbatim, publishing reads this console
  // deliberately never declared. Upstream parses both ids with `Number(...)`
  // and refuses anything else, so digits are the entire legitimate space.
  { path: "/api/admin/documents/:id{[0-9]+}", query: [] },
  { path: "/api/admin/documents/:id{[0-9]+}/versions", query: [] },
  // The document preview and download. Upstream this is two SELECTs and an
  // HMAC over the row's own storage path — no write, no queue, no state — and
  // it is the only way a read-only console can show the bytes at all. Its
  // sibling `signed-url-by-path` signs whatever R2 key the CALLER names and is
  // deliberately left out: that one is not a read of a record the console
  // already has, it is an object-store key oracle. Static, so `orderedRoutes()`
  // sorts it ahead of the `:id` entry.
  { path: "/api/admin/documents/signed-url", query: ["id"] },
  // Declared in wave 5 for the Operations screen's R2 view: an R2 `list()`
  // cross-referenced against the `documents` table, which is the only storage
  // read ProductData serves under /api/admin. It is a static segment, so
  // `orderedRoutes()` sorts it ahead of the `:id` entry — and the digit
  // constraint above means it could never have ridden that entry anyway.
  { path: "/api/admin/documents/storage-readiness", query: [] },

  // One screen, three views. `scope=companies` switches the inbox list to the
  // per-company mailroom roster, and `status` filters that roster; both answer
  // with real rows since goal 209 moved the Stripe mailroom price registry into
  // ProductData itself (`mailroom/routes.ts` calls `getMailroomPriceLists`), so
  // the exclusion that used to sit here no longer describes anything.
  // `auth_user_id` stays undeclared: the screen offers no control for it.
  {
    path: "/api/admin/mailroom",
    query: [
      ...PAGINATION,
      "search",
      "company_id",
      "category",
      "scope",
      "status",
    ],
  },
  // The third view. A static segment, declared ABOVE the `:id` line so the file
  // reads in match order — `orderedRoutes()` would sort it there anyway, and
  // the digit constraint already forecloses the collision.
  {
    path: "/api/admin/mailroom/subscriptions",
    query: [...PAGINATION, "status", "search"],
  },
  { path: "/api/admin/mailroom/:id{[0-9]+}", query: [] },

  // Two global DocuSeal reads: the D1 health aggregate and the cross-company
  // submissions list (paged, optional status). Per-company submissions still
  // ride the wave-2 `/companies/:companyId/docuseal-submissions` entry above.
  { path: "/api/admin/docuseal/health", query: [] },
  { path: "/api/admin/docuseal/submissions", query: [...PAGINATION, "status"] },

  // --- federal tax, state tax, formation, fax (wave 4) ---------------------
  // Unlike documents and mail, every id here is a TEXT column upstream
  // (`orders.id` is `ord_<uuid>`, `fax_submissions.id` is TEXT) and no handler
  // parses one with `Number(...)`. A `{[0-9]+}` constraint would therefore
  // 404 every real id, so these params stay bare and the collisions are closed
  // structurally instead — see each entry.
  // One path, two handlers upstream. `?view=triage` swaps the plain queue for
  // the tax team's worklist — next action, customer progress, document status
  // and deadline per row, plus `tabCounts` — and it is the same read-only GET.
  // Its page size is `limit` (it never reads `pageSize`), so both spellings are
  // declared: the plain branch takes `pageSize`, the triage branch `limit`.
  {
    path: "/api/admin/federal-tax/queue",
    query: [
      ...PAGINATION,
      "status",
      "tax_year",
      "sort_by",
      "sort_order",
      "view",
      "tab",
      "search",
      "limit",
    ],
  },
  { path: "/api/admin/federal-tax/filing/stats", query: [] },
  // One SELECT over the open filings, most urgent first. `companyId` narrows
  // it to one company for the company panel; without it the worklist gets
  // every open filing. It sits a level below `filing/`, so it is a static
  // path of its own rather than anything that could ride `:orderId`.
  { path: "/api/admin/federal-tax/filing/deadlines", query: ["companyId"] },
  // `queue` and `filing` are the only static siblings upstream serves at this
  // depth, and both are declared above — `orderedRoutes()` sorts them ahead,
  // so nothing static can ride this entry with a GET.
  { path: "/api/admin/federal-tax/:orderId", query: [] },
  // The packet viewer (spec 2026-09-26 §10.2): every packet version with
  // signed URLs for its unsigned, signed-upload and final documents. The entry
  // stops at `/packets` on purpose — the depth rule above: nothing deeper is
  // declared, so `/packets/:packetId/{review,mailed}` and `/packets/rebuild`
  // (POST-only upstream, reached only through `federal-tax-writes.ts`) fit no
  // GET entry, and `/:orderId/fax-proof` stays undeclared beside it.
  { path: "/api/admin/federal-tax/:orderId/packets", query: [] },

  // State filings (spec 2026-09-30 section 5.6): the queue of state-tax
  // ORDERS across every company, and one order's detail (its `service_data`,
  // the state facts, the deep link to the state's site and the moves it
  // allows). The collection sits at the prefix itself, one segment shorter than
  // `/:orderId`, so the two cannot collide.
  //
  // `:orderId` is bare (`orders.id` is `ord_<uuid>`, so a digit constraint would
  // 404 every real id) and only the literal `wyoming-filing` and
  // `delaware-payment` reads are deeper: the
  // eight write routes beneath it (`start-filing`, `mark-filed`, `confirm-quote`, `send-back`,
  // `record-payment`, `close`, `proof`, `proof/presign`) are POSTs the console's
  // State filings write exception owns in `state-filings-writes.ts`, and none is
  // reachable as a GET.
  //
  // The one sibling the API serves beside the id is `documents/:documentId`, a
  // signed link for a proof document (numeric id) or an archived registry
  // report (UUID). It is a read that signs a URL and writes nothing, and the id
  // is hex digits and dashes (a digit run is one; so is a UUID), so it carries
  // that pattern and the API checks the exact shape. No alternation or `{n}`
  // quantifier inside the braces: Hono compiled `[0-9]+|...{36}` so that a UUID
  // matched on its first digit. `documents` is listed in
  // `upstream-siblings.ts` so it can never be read as an order id.
  {
    path: "/api/admin/state-filings",
    query: [
      ...PAGINATION,
      "status",
      "state",
      "needs",
      "company_id",
      "taxYear",
      "search",
    ],
  },
  {
    path: "/api/admin/state-filings/documents/:documentId{[0-9a-fA-F-]+}",
    query: [],
  },
  // The Upcoming view: every Wyoming and Delaware company's NEXT state filing
  // (not an order list), computed and paged by the API. A static sibling of
  // `/:orderId`, sorted ahead of it by `orderedRoutes()` and listed in
  // `upstream-siblings.ts`, so "upcoming" is never read as an order id.
  {
    path: "/api/admin/state-filings/upcoming",
    query: [...PAGINATION, "state", "window", "search"],
  },
  { path: "/api/admin/state-filings/:orderId", query: [] },
  // The Wyoming annual report card's read; `file-wyoming` is a POST (state-filing-run-writes.ts).
  { path: "/api/admin/state-filings/:orderId/wyoming-filing", query: [] },
  // The Delaware payment card's read; `pay-delaware` is a POST (state-filing-run-writes.ts).
  { path: "/api/admin/state-filings/:orderId/delaware-payment", query: [] },

  // Formation's cross-company read, and the detail behind it.
  {
    path: "/api/admin/formation",
    query: [
      ...PAGINATION,
      "formation_state",
      "company_id",
      "search",
      "payment_scope",
      "stage_group",
    ],
  },
  // The detail stops here, one level above everything the id sits over:
  // `verify`, `request-signature`, `regenerate-signature`, `file-wyoming`,
  // `extraction-warning`, `sos-filing`, `send-ss4` and `upload-articles`
  // are all POST upstream, and `ss4-context` is a GET that 400s when a company
  // has no shareholders and answers with shareholder PII. Depth is the defence
  // here as it is for fax and webhooks — an order id is `ord_<uuid>`, so no
  // digit constraint separates it from a word.
  //
  // Unlike fax and webhooks, this prefix also carries upstream STATIC siblings
  // — `by-company/:companyId`, `certificate/upload`, `ein/upload` — which sit
  // BESIDE the id rather than below it, where depth cannot reach them.
  // `upstream-siblings.ts` carries the `/api/admin/formation` key that shadows
  // all three; without it `/formation/certificate` would be forwarded as a
  // request for the formation order whose id is "certificate".
  { path: "/api/admin/formation/:id", query: [] },
  // The SS-4 wet-signature review's read: every SS-4 version with signed URLs
  // for the unsigned PDF, the customer's upload and the approved scan. The one
  // entry below the formation id, and it is a LITERAL `ss4` — a literal
  // matches only itself, so `ss4-context` and every POST sibling above still
  // fit no entry. It stops at `/ss4` for the depth rule:
  // `/ss4/:packetId/review` is POST-only upstream and reached only through
  // `formation-writes.ts`.
  { path: "/api/admin/formation/:id/ss4", query: [] },
  // The Wyoming online filing card's read: the order's name check and the
  // filing job's state with 10-minute signed links to its screenshots and
  // documents. A literal `wyoming-filing`, like `ss4` above: nothing else below
  // the id fits, and the POSTs beside it (`check-name`, `file-wyoming`) are the
  // operator's, never the console's.
  { path: "/api/admin/formation/:id/wyoming-filing", query: [] },

  // Company services (registered agent, mailroom). These are services with a
  // start, a renewal date and an end, not orders, so the old order-keyed
  // `/registered-agent` reads are gone and this pair replaced them. Both are
  // pure SELECTs upstream (`company-services/admin-read-routes.ts`) and the
  // detail is the last segment: nothing below the id is declared, and the only
  // writes (renew, status) are the operator's, on the company routes.
  {
    path: "/api/admin/company-services",
    query: [...PAGINATION, "serviceType", "status", "search"],
  },
  { path: "/api/admin/company-services/:serviceId", query: [] },
  // The change-of-registered-agent checklist for one order (details, signature,
  // filled forms, proof). A pure read upstream; the two writes behind it are
  // `ra-change-writes.ts`.
  { path: "/api/admin/ra-change/:orderId", query: [] },

  // Fax stops at the detail, and that depth limit is the read-only boundary,
  // not a scope choice: `GET /fax/:faxId/proof-pdf` ENQUEUES a PDF job
  // upstream — a mutation wearing a GET — and any `/fax/:faxId/:something`
  // entry would match it. Declaring nothing deeper keeps it, `/fax/download/
  // :faxId` and `/fax/:faxId/preview` all out. `catalog.test.ts` pins the
  // depth so a later entry cannot quietly reopen it.
  {
    path: "/api/admin/fax",
    query: [
      ...PAGINATION,
      "direction",
      "status",
      "document_type",
      "company_id",
      "search",
      "unassigned",
    ],
  },
  { path: "/api/admin/fax/:faxId", query: [] },

  // --- stripe/billing, webhooks, notifications, operations (wave 5) --------
  // Every Stripe read is a fully static segment, so this whole block has no
  // collision surface at all. `sync` is the one that repays a second look:
  // upstream serves a read-only status on GET and ENQUEUES a customer sync on
  // POST, at the same path. The console registers GET handlers only, so the
  // POST is unreachable from here — `index.test.ts` pins that directly rather
  // than leaving it to the shape of this list.
  //
  // Three of the lists now have a detail sibling, each declared AFTER its own
  // list (ordering is enforced by `orderedRoutes()` regardless, but the list
  // should read the way the router resolves). None of the three ids can be
  // constrained: an invoice id is either an `invoices` row id or a paid
  // ORDER's id, because the list UNIONs them; a subscription id is either the
  // canonical TEXT id or the legacy table's integer PK, so a digit constraint
  // would 404 one grain and a text-only one the other. There is nothing to
  // constrain them against — and nothing to collide with either: upstream
  // serves no static segment under any of the three prefixes, which
  // `upstream-siblings.ts` records and `upstream-siblings.test.ts` rechecks
  // against ProductData's own source.
  { path: "/api/admin/stripe/overview", query: [] },
  { path: "/api/admin/stripe/subscriptions", query: [...PAGINATION] },
  { path: "/api/admin/stripe/subscriptions/:id", query: [] },
  { path: "/api/admin/stripe/invoices", query: [...PAGINATION] },
  { path: "/api/admin/stripe/invoices/:id", query: [] },
  { path: "/api/admin/stripe/payment-intents", query: [...PAGINATION] },
  { path: "/api/admin/stripe/payment-intents/:id", query: [] },
  { path: "/api/admin/stripe/transactions", query: [...PAGINATION] },
  // The refunds LIST, not a refund affordance: issuing one is a POST on this
  // path and an excluded MCP action. An operator needs to see the refunds that
  // happened; the console gives them no way to make another.
  { path: "/api/admin/stripe/refunds", query: [...PAGINATION] },
  // The disputes LIST — read-only, same as refunds: `POST .../disputes/sync`
  // stays an operator/agent action, never a console affordance.
  { path: "/api/admin/stripe/disputes", query: [...PAGINATION] },
  // The saved evidence draft for one dispute (refund-policy spec, phase D). It
  // stops at `/evidence`: the submit and rebuild POSTs below it are human
  // console writes reached only through `dispute-writes.ts`, never as a GET.
  { path: "/api/admin/stripe/disputes/:id/evidence", query: [] },
  { path: "/api/admin/stripe/payouts", query: [...PAGINATION] },
  { path: "/api/admin/stripe/payouts/:id", query: [] },
  {
    path: "/api/admin/stripe/balance-transactions",
    query: [...PAGINATION, "type", "payoutId"],
  },
  { path: "/api/admin/stripe/balance", query: [] },
  { path: "/api/admin/stripe/sync", query: [] },
  {
    path: "/api/admin/stripe/checkout-recoveries",
    query: [...PAGINATION],
  },
  {
    path: "/api/admin/stripe/fraud-warnings",
    query: [...PAGINATION],
  },

  // Webhooks stop at the detail, for the same reason fax does. A webhook id is
  // an integer PK OR a `evt_…`/external event id — upstream accepts both — so
  // the digit constraint documents and mail use would 404 every Stripe event
  // id. Depth is the defence instead: `POST /webhooks/:id/retry` re-enqueues a
  // delivery, and no `/webhooks/:id/:something` entry exists to match it.
  {
    path: "/api/admin/webhooks",
    query: [
      ...PAGINATION,
      "source",
      "processing_status",
      "event_type",
      "search",
      "window",
    ],
  },
  { path: "/api/admin/webhooks/:id", query: [] },

  // Notifications: `channel` and `recipient_email` are read off the query
  // upstream and then ignored — the staging table has neither column, and the
  // handler says so in a comment while selecting `NULL AS channel`. Declaring
  // them would ship two filter controls that quietly match everything.
  // `notification_type` IS a column, and is what a template detail page
  // narrows its own deliveries with.
  {
    path: "/api/admin/notifications",
    query: [
      ...PAGINATION,
      "status",
      "notification_type",
      "recipient_auth_user_id",
    ],
  },
  // The template CATALOGUE and the rendered email behind one template — the
  // two reads the old `/admin/notifications` page was, and the console's
  // delivery log is not. Both are fully static segments beside
  // `registry-stats`, so neither can shadow the other and no `:param` sits
  // under `/notifications` for them to ride.
  //
  // `preview` is the one entry in this file whose path upstream also answers
  // on POST (a body-driven render, not the id-driven one). That is the
  // `/stripe/sync` shape exactly: the Worker registers `app.get` and nothing
  // else, and `index.test.ts` fires a real POST at it to prove so. The other
  // twin, `POST /notifications/test`, ENQUEUES a send and has no entry whose
  // shape it fits.
  { path: "/api/admin/notifications/catalogue", query: [] },
  { path: "/api/admin/notifications/preview", query: ["id"] },
  { path: "/api/admin/notifications/registry-stats", query: [] },

  // The Operations screen's four remaining reads. `naics/coverage` is new in
  // this wave (ProductData had only `POST /naics/backfill`); the sibling POST
  // is a different static segment, so neither can shadow the other.
  { path: "/api/admin/monitoring/data-integrity", query: [] },
  { path: "/api/admin/naics/coverage", query: [] },
  // The agent-ops alert feed (goal 233; the `/alerts` screen is goal 234): the
  // `ops_alerts` rows `enqueueAgentOpsAlert` writes for every failed agent
  // operation it pages about, newest first. A fully static path — ProductData
  // serves nothing else under `/ops/`, so there is no sibling to shadow and
  // no `:param` to constrain — and one pure SELECT upstream: no write, no
  // queue, so the read-only marker is served rather than refused.
  // `reason` is a real upstream clause as of A3-08 — the screen used to filter
  // the loaded page in the browser, which lies at page two.
  { path: "/api/admin/ops/alerts", query: [...PAGINATION, "reason"] },
  {
    path: "/api/admin/trustpilot/invitation-history",
    query: [...PAGINATION, "status", "search"],
  },
  // The "Send invitations" picker: paid customers by name, email or company
  // id, each flagged `alreadyInvited`. One SELECT upstream. The sibling
  // `send-invitations` is a POST with its own console exception
  // (`trustpilot-writes.ts`) and is a different static segment.
  {
    path: "/api/admin/trustpilot/customer-search",
    query: [...PAGINATION, "q", "company_id"],
  },
  ...SPLIT_READ_ROUTES,
  // The payments ledger, and the one detail entry in this catalog whose
  // pattern is a REFUSAL rather than a shape.
  //
  // `POST /api/admin/payments/manual` is the only other segment at this depth,
  // and a bare `/payments/:id` would fit it exactly — which is why the detail
  // was left out until now. A payment id is a UUID or a `pay_…` TEXT id, so
  // there is no positive constraint to write; the constraint that works is the
  // negative one, and `(?!manual$)` refuses precisely the literal `manual` and
  // nothing else. `index.test.ts` pins BOTH halves — that `…/payments/manual`
  // 404s and that a real id still forwards — because a lookahead that silently
  // stopped matching would be a live mutation path with a green suite.
  //
  // The mirror in `upstream-siblings.ts` refuses `manual` a second time, at the
  // param, so the two defences fail independently.
  {
    path: "/api/admin/payments",
    query: [...PAGINATION, "method", "status", "search"],
  },
  {
    path: "/api/admin/payments/:id{(?!manual$)[A-Za-z0-9_:-][A-Za-z0-9_.:-]*}",
    query: [],
  },

  // The approval review screen (plan 05). The collection carries the status
  // filter and paging upstream honours; the detail stops at the id — the
  // decision POSTs below it are this Worker's own session-only routes and are
  // reachable only through `approval-decisions.ts`, never the read forwarding.
  {
    path: "/api/admin/approvals/requests",
    query: ["status", ...PAGINATION],
  },
  { path: "/api/admin/approvals/requests/:id", query: [] },
  {
    path: "/api/admin/state-registry/discrepancies",
    query: ["status", "state", ...PAGINATION],
  },

  // --- accounting: the bookkeeping ledger (plans 01-05) --------------------
  // The books list is what makes every other entry here reachable: each report
  // route is keyed on a book id, and ProductData had no way to list one until
  // this wave added `GET /api/admin/bookkeeping/books`.
  //
  // The review queue stops at the collection. Its two decision POSTs
  // (`/review/:decisionId/{confirm,ask-customer}`) sit BELOW it, and they are
  // this Worker's own session-only routes in `review-decisions.ts` — reachable
  // only there, never through the read forwarding, exactly like the approval
  // decisions above.
  // `bookId` scopes the list to one book: the console refreshes the open
  // book's counts after a decision without recounting the fleet.
  { path: "/api/admin/bookkeeping/books", query: ["bookId"] },
  // `bookId` scopes the queue to one company's book: the cross-estate queue
  // carried every customer's samples at once and froze the screen.
  // `q` searches the lines and keeps each matching vendor's whole group.
  { path: "/api/admin/bookkeeping/review", query: ["limit", "bookId", "q"] },
  // Every pending line of one review group (round 3, F5): the list carries
  // five samples per group, and a reviewer deciding a group must see all of
  // it. A static segment beside the collection; the decision POSTs stay this
  // Worker's own session routes.
  {
    path: "/api/admin/bookkeeping/review/group",
    query: ["bookId", "groupKey", "cursor", "limit"],
  },
  { path: "/api/admin/feed/connections", query: [] },
  // The four report reads. Every one is a PURE read upstream: the disclosure's
  // rate SOURCING — which takes a lock, calls a provider, writes the rate cache
  // and can page ops — is a separate POST
  // (`/reports/foreign-balances/refresh-rates`) precisely so these stay
  // forwardable by a read-only console. That POST sits one segment BELOW the
  // deepest entry declared here, and this Worker registers GET only, so two
  // independent defences keep it out.
  // One book's chart of accounts. It is what turns a review candidate's bare
  // code into a category name a reviewer can decide on, and it cannot be a
  // static copy in the console: the template seeds most of the chart, but bank
  // and card accounts are allocated per book when a feed is linked, so a copy
  // would be missing exactly the accounts the balance difference runs on.
  {
    path: "/api/admin/bookkeeping/books/:bookId/accounts",
    query: ["includeArchived"],
  },
  {
    path: "/api/admin/bookkeeping/books/:bookId/balance-difference",
    query: [],
  },
  // The book's Overview (round 3, F7/F9): a card per bank account (statement
  // balance against the books), the task list, six months of cash in and out
  // and net profit to date. The Transactions header reads the same payload.
  { path: "/api/admin/bookkeeping/books/:bookId/overview", query: [] },
  // Round 4 reads. Static segments (`periodic` is not a `ReportName`, so it
  // cannot ride the generic report entries), each one pure SELECTs upstream.
  // M3: the Home dashboard for a date range. M5: the book's accounts list.
  {
    path: "/api/admin/bookkeeping/books/:bookId/dashboard",
    query: ["from", "to"],
  },
  { path: "/api/admin/bookkeeping/books/:bookId/accounts-overview", query: [] },
  // M4's admin twin: the book's transactions, read-only, and one line with its
  // journal. `:feedLineId` is a single segment under a static prefix, so it
  // cannot ride `/lines` or any report entry.
  {
    path: "/api/admin/bookkeeping/books/:bookId/transactions",
    query: [
      "account",
      "type",
      "method",
      "status",
      "from",
      "to",
      "q",
      "cursor",
      "limit",
    ],
  },
  {
    path: "/api/admin/bookkeeping/books/:bookId/transactions/:feedLineId{[A-Za-z0-9_:-]+}",
    query: [],
  },
  // M1: a report with one column per month, quarter or year.
  {
    path: "/api/admin/bookkeeping/books/:bookId/reports/periodic",
    query: ["report", "year", "grain", "basis"],
  },
  // M2: one bank account's reconciliation months, and one month's lines.
  // `:month` is YYYY-MM so a word cannot ride the segment.
  {
    path: "/api/admin/bookkeeping/books/:bookId/accounts/:feedAccountId{[A-Za-z0-9_:-]+}/reconciliation",
    query: ["year"],
  },
  {
    path: "/api/admin/bookkeeping/books/:bookId/accounts/:feedAccountId{[A-Za-z0-9_:-]+}/reconciliation/:month{[0-9]{4}-[0-9]{2}}",
    query: ["cursor", "limit"],
  },
  // Rules worth making, from confirmed history and the queue's AI picks
  // (round 3, F12). Pure SELECTs; creating one is the existing rule POST.
  {
    path: "/api/admin/bookkeeping/books/:bookId/rule-suggestions",
    query: [],
  },
  {
    path: "/api/admin/bookkeeping/books/:bookId/reports/balance-sheet",
    query: ["asAt"],
  },
  {
    path: "/api/admin/bookkeeping/books/:bookId/reports/foreign-balances",
    query: ["asAt"],
  },
  {
    path: "/api/admin/bookkeeping/books/:bookId/reports/profit-and-loss",
    query: ["from", "to", "basis", "priorFrom", "priorTo"],
  },
  // The shipped charts of accounts, and the captured benchmark beside them
  // (plan 18). Global rather than per book: they answer what a NEW book starts
  // with, which the per-book chart read cannot.
  //
  // `:templateId` is constrained to the closed union rather than mirrored in
  // `upstream-siblings.ts`. The set is three literals that change only when
  // ProductData's own registry changes, so the pattern is exact — a sibling
  // mirror would be a weaker version of the same guard.
  { path: "/api/admin/bookkeeping/templates", query: [] },
  {
    path: "/api/admin/bookkeeping/templates/:templateId{(?:service|ecommerce|mercury-standard)}",
    query: [],
  },
  // The proposed and confirmed transfer pairs (plan 07). The Reconcile tab's
  // Transfer panel lists them; the two decisions below this path are POSTs in
  // `worker/bookkeeping-writes.ts`, and depth is the second defence after
  // method — nothing under `/transfers/:transferId` is declared as a read.
  { path: "/api/admin/bookkeeping/books/:bookId/transfers", query: ["state"] },

  // The cockpit's own reads (plan 14). Each is a pure SELECT upstream: every
  // bookkeeping mutation is a POST in `worker/bookkeeping-writes.ts`, and this
  // Worker registers GET here, so the two surfaces cannot be confused.
  //
  // The lines read is the one every cockpit tab is built on — reconcile, cash
  // coding and statements are the same rows under different filters, which is
  // why its query allowlist is the longest in this file. `counterLegFor` is the
  // transfer search: the matching rules (equal magnitude, opposite sign, a
  // different feed account, inside a window, not already claimed) belong with
  // the detector that wrote them, so the browser names a line and the API
  // answers the candidates.
  {
    path: "/api/admin/bookkeeping/books/:bookId/lines",
    query: [
      "feedAccountId",
      "status",
      "reconciliation",
      "source",
      "pending",
      "from",
      "to",
      "amountMin",
      "amountMax",
      "amountAbs",
      "q",
      "category",
      "counterLegFor",
      "exceptTransferId",
      "id",
      "includeExcluded",
      "contactId",
      "cursor",
      "limit",
    ],
  },
  // The contact picker on the coding forms. A contact is never derived from
  // payee text (spec §2), so this list IS the set of choices.
  {
    path: "/api/admin/bookkeeping/books/:bookId/contacts",
    query: ["kind", "q", "includeArchived"],
  },
  // The event log, read on the Overview tab as "what happened last". Plan 16
  // builds the org-wide feed on the same shape.
  //
  // Plan 14 declared this for the Overview strip's "last events". The History
  // tab (plan 16) adds a date range and a subject filter, and an undeclared
  // key is dropped before the forward — so a date control whose key is not
  // here silently matches everything.
  {
    path: "/api/admin/bookkeeping/books/:bookId/events",
    query: [
      "kinds",
      "actor",
      // The History tab's actor dropdown (round 3, F16).
      "actorKind",
      "subjectType",
      "subjectId",
      "from",
      "to",
      "limit",
      "cursor",
    ],
  },
  // Statement imports: the batch list and the one batch being mapped. Nothing
  // deeper is declared — `stage`, `commit` and `discard` are POSTs one level
  // below, and depth is the second defence after method. The batch id is
  // constrained to its `ib_` shape because the sibling mirror never surveys a
  // prefix that already holds a `:param`.
  { path: "/api/admin/bookkeeping/books/:bookId/imports", query: ["status"] },
  {
    path: "/api/admin/bookkeeping/books/:bookId/imports/:batchId{ib_[0-9a-f-]+}",
    query: [],
  },
  // The report engine's own reads (spec §8), one entry per report NAME, and
  // the ONE place in this file where a path segment is snake_case. That is
  // deliberate and not a slip: the engine serves a generic `…/reports/:report`
  // whose `:report` is a `ReportName` verbatim — `general_ledger`,
  // `account_transactions`, `trial_balance`, `journal`, `cash_summary`, plus
  // `profit_and_loss` and `balance_sheet` — precisely so it can never collide
  // with the v1 kebab routes above (`profit-and-loss`, `balance-sheet`,
  // `foreign-balances`), which keep their plan-05 handlers and their plan-05
  // shapes. Two spellings, two generations of route, no overlap.
  ...reportRoutes(),

  // The two tax support reads (spec §9.4). Static segments, so they sort ahead
  // of nothing — there is no `:report` param for them to ride — and each is one
  // pure SELECT upstream over a fiscal year.
  {
    path: "/api/admin/bookkeeping/books/:bookId/reports/form-5472-support",
    query: ["fiscalYear"],
  },
  {
    path: "/api/admin/bookkeeping/books/:bookId/reports/vendor-forms",
    query: ["fiscalYear"],
  },

  // Year-end close (spec §9). The LIST is one SELECT over `ledger_year_close`;
  // the readiness read is the per-year checklist the Close year sheet refuses
  // to open without. Everything that DECIDES a close is a POST — `…/close/
  // :fiscalYear` and `…/close/:fiscalYear/reopen`, both plan 14's write-path
  // routes — and this Worker registers `app.get` for the catalog, so no read
  // here can reach one; the depth limit is the second defence and
  // `catalog.test.ts` pins it.
  // The book's two lock dates (spec §5.3). Its own read because the close list
  // does not carry them: `GET …/close` answers `{ closes }`, and a lock form
  // fed from that payload renders empty and asks to CLEAR both locks on Save.
  // The matching POST is plan 14's write-path route, never this one.
  { path: "/api/admin/bookkeeping/books/:bookId/locks", query: [] },
  { path: "/api/admin/bookkeeping/books/:bookId/close", query: [] },
  {
    // The one id in this catalog whose whole legitimate space is four digits.
    path: "/api/admin/bookkeeping/books/:bookId/close/:fiscalYear{[0-9]{4}}/readiness",
    query: [],
  },

  // --- accounting cockpit B: rules, chart, contacts, journals (plan 15) -----
  //
  // Every `:param` below is CONSTRAINED rather than mirrored in
  // `upstream-siblings.ts`, and that is forced rather than chosen:
  // `upstream-siblings.test.ts` asserts no mirrored prefix sits inside another,
  // and every prefix these routes would need is nested under the existing
  // `/api/admin/bookkeeping/books` key. A pattern is the remaining defence, and
  // the ledger's prefixed ids (`je_…`, `acct_…`, apps/api/src/ledger/ids.ts)
  // make most of them exact.
  //
  // Contacts is the one that cannot be spelled by shape: plan 06 has not fixed
  // a contact-id prefix, and `POST …/contacts/merge` sits at the id's own
  // depth. So the param REFUSES that literal, the same shape
  // `/api/admin/payments/:id{(?!manual$)…}` uses.
  // `%` is in the class alongside the literal punctuation: `contactQuery`
  // `encodeURIComponent`s the id, and an id carrying a character outside the
  // unreserved set (rare, but this store never fixed a charset) arrives
  // percent-escaped — a class that refused `%` would 404 exactly the id it
  // was built to admit. The FIRST character is never `.` or `%`, so no
  // spelling of `.` or `..` fits the id position.
  {
    path: "/api/admin/bookkeeping/books/:bookId/contacts/:contactId{(?!merge$)[A-Za-z0-9_:-][A-Za-z0-9_.:%-]*}",
    query: [],
  },
  // Book rules AND global rules in one answer, each row carrying its own
  // `bookId` (null = every book). One read, because the editor's ordering
  // question — "what runs before this" — spans both sets.
  { path: "/api/admin/bookkeeping/books/:bookId/rules", query: [] },
  // The global set on its own, for the section that edits them outside a book.
  { path: "/api/admin/bookkeeping/rules", query: [] },
  // Vendors coded the same way across companies: global rules worth making.
  { path: "/api/admin/bookkeeping/rules/suggestions", query: [] },
  {
    path: "/api/admin/bookkeeping/books/:bookId/journals",
    // `origin=manual` is the Journals tab's default (round 3, F13).
    query: ["from", "to", "source", "q", "limit", "cursor", "origin"],
  },
  {
    path: "/api/admin/bookkeeping/books/:bookId/journals/:entryId{je_[0-9a-f-]+}",
    query: [],
  },
  // Two reads of one ledger account. `activity`: the three counts that decide
  // whether it CAN be archived, read before the button is offered rather than
  // discovered by a refusal. `opening-balance`: a foreign-currency account's
  // live opening, or the spot rate its USD basis defaults to (NT-80). One
  // entry, the alternation wrapped like `events` below.
  {
    path: "/api/admin/bookkeeping/books/:bookId/accounts/:accountId{acct_[0-9a-f-]+}/:accountRead{(?:activity|opening-balance)}",
    query: [],
  },
  // One subject's events and notes. `subject_type` is a closed set in the spec
  // (§4.1), so the catalog spells it out and refuses everything else — a param
  // that admitted any word would publish a read keyed on a caller's string.
  // The alternation is WRAPPED: catalog readers join segments with `/` before
  // compiling, and an ungrouped `a|b` would bind across the slash.
  {
    path: "/api/admin/bookkeeping/books/:bookId/events/:subjectType{(?:feed_line|journal_entry|coding_rule|ledger_contact|ledger_account|ledger_period|ledger_book|feed_connection|import_batch)}/:subjectId{[A-Za-z0-9_:-][A-Za-z0-9_.:-]*}",
    query: ["limit", "cursor"],
  },
];

/**
 * Static segments must be registered before their `:param` sibling, or Hono
 * matches `/orders/tab-counts` against `/orders/:id` and the console silently
 * asks ProductData for an order whose id is "tab-counts". Sorting by parameter
 * count keeps that ordering true no matter how the list above is edited.
 */
export function orderedRoutes(
  routes: readonly ReadRoute[] = READ_ROUTES,
): readonly ReadRoute[] {
  return [...routes].sort((a, b) => countParams(a.path) - countParams(b.path));
}

function countParams(path: string): number {
  return path.split("/").filter((segment) => segment.startsWith(":")).length;
}

/**
 * Values each of a route's `:param` segments must refuse, keyed by param name.
 *
 * Ordering solves half the overmatch problem: a static sibling the catalog
 * DECLARES is registered ahead of its `:param` and matches its own entry. It
 * does nothing for a static sibling the catalog does NOT declare — that one
 * has no entry to match, so it falls through to the `:param` and is forwarded
 * with the segment sitting in the id position. `/api/admin/users/role` is that
 * case, and it cannot be fixed with a pattern constraint because a Better Auth
 * user id is text.
 *
 * So the param refuses the upstream static segments at its own position,
 * whether or not the catalog declares them: a declared one never reaches the
 * param handler anyway (ordering), and refusing it here means the ordering
 * rule failing would produce a 404 rather than a forwarded request for an
 * order whose id is "work-queue".
 *
 * Reads the mirror by the prefix ABOVE the param, so it holds wherever the
 * param sits — `/companies/:companyId/documents` is checked against the
 * `/api/admin/companies` sibling set, not the `/documents` one.
 */
export function shadowedParamValues(
  route: ReadRoute,
): ReadonlyMap<string, ReadonlySet<string>> {
  const segments = route.path.split("/");
  const shadowed = new Map<string, ReadonlySet<string>>();

  segments.forEach((segment, index) => {
    if (!segment.startsWith(":")) return;
    const siblings =
      UPSTREAM_STATIC_SIBLINGS[segments.slice(0, index).join("/")];
    if (siblings && siblings.length > 0) {
      shadowed.set(paramName(segment), new Set(siblings));
    }
  });

  return shadowed;
}

/**
 * The name Hono matches a segment under, without the pattern a constrained
 * param carries: `:id{[0-9]+}` is reported as `id`. Reading the raw segment
 * instead would look up a key that does not exist and forward an empty
 * segment — a silent request for document "" rather than a 404.
 */
export function paramName(segment: string): string {
  const name = segment.slice(1);
  const brace = name.indexOf("{");
  return brace === -1 ? name : name.slice(0, brace);
}
